import styled from 'styled-components';
import classNames from 'classnames';
import { useState, useEffect } from 'react';
import ServerStylesConditionalWrapper from './ServerStylesConditionalWrapper';
import { device } from '../../utils/styled-component-mixins';
import ButtonLink from './ButtonLinkComponent';
import useMediaQuery from '../custom-hooks/useMediaQuery';

const PaginationStyled = styled.div`
  display: flex;
  justify-content: center;

  .pagination-wrapper {
    column-gap: 2.4rem;
    display: flex;
    justify-content: center;

    @media ${device.desktop} {
      column-gap: 1.6rem;
    }

    .btn-wrapper {
      height: 2.4rem;
      width: 2.4rem;

      @media ${device.desktop} {
        height: 4rem;
        width: 4rem;
      }

      .material-icons-round {
        margin-bottom: 0;

        @media ${device.desktop} {
          font-size: 3.6rem;
        }
      }

      .btn {
        width: 100%;
      }
    }
  }

  ul {
    column-gap: 2.4rem;
    display: flex;
    height: 2.4rem;
    justify-content: center;

    @media ${device.desktop} {
      column-gap: 1.6rem;
      height: auto;
    }

    li {
      @media ${device.deviceOnly} {
        align-items: center;
        display: flex;
        justify-content: center;
      }
    }

    button {
      color: var(--cta-blue);
      border: none;
      font-size: 1.4rem;
      font-weight: 400;
      height: 2.4rem;
      width: 2.4rem;

      @media ${device.deviceOnly} {
        align-items: center;
        display: flex;
        justify-content: center;
      }

      @media ${device.desktop} {
        font-size: 1.6rem;
        height: 4rem;
        width: 4rem;
      }

      &:hover:not(.active) {
        color: var(--slate);
        font-size: 1.8rem;
        font-weight: 700;
      }

      &.active {
        background-color: var(--cta-blue);
        border-radius: 10rem;
        color: var(--white);
        font-weight: 700;
      }
    }
  }
`;

const Pagination = (props) => {
  const isDesktop = useMediaQuery(device.desktop);
  const { numberOfPages, currentPage, fetchPageData } = props;
  const [pagesToShow, setPagesToShow] = useState([]);
  const [showLeftEllipsis, setShowLeftEllipsis] = useState(false);
  const [showRightEllipsis, setShowRightEllipsis] = useState(false);
  const [pages, setPages] = useState([]);
  const [currentPageState, setCurrentPageState] = useState(currentPage);

  useEffect(() => {
    setCurrentPageState(currentPage);
  }, [currentPage]);

  const handlePaginationRendering = () => {
    const paginationPagesToShow = isDesktop ? 5 : 3;
    if (numberOfPages <= paginationPagesToShow || currentPageState <= paginationPagesToShow) {
      setPagesToShow(pages.slice(0, paginationPagesToShow));
    } else if (currentPageState > pagesToShow[pagesToShow.length - 1]) {
      setPagesToShow(
        pages.slice(currentPageState - 1, currentPageState + (paginationPagesToShow - 1))
      );
    } else if (currentPageState < pagesToShow[0]) {
      setPagesToShow(pages.slice(currentPageState - paginationPagesToShow, currentPageState));
    }
  };

  const handlePaginationClick = (page) => {
    fetchPageData(page);
    setCurrentPageState(page);
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
  };

  useEffect(() => {
    setShowLeftEllipsis(pagesToShow[0] > 1);
    setShowRightEllipsis(pagesToShow[pagesToShow.length - 1] < numberOfPages);
  }, [pagesToShow, numberOfPages]);

  useEffect(() => {
    handlePaginationRendering();
  }, [currentPageState, numberOfPages, pages]);

  useEffect(() => {
    const pagesArr = [];
    // eslint-disable-next-line no-plusplus
    for (let i = 1; i <= numberOfPages; i++) {
      pagesArr.push(i);
    }
    setPages(pagesArr);

    handlePaginationRendering();
  }, [numberOfPages]);

  return (
    <ServerStylesConditionalWrapper>
      <PaginationStyled>
        <div className="pagination-wrapper">
          {currentPageState > 1 && (
            <ButtonLink
              onClickFunction={() => handlePaginationClick(currentPageState - 1)}
              replacementTertiaryIcon="chevron_left"
              type="tertiary"
              isButtonTag={true}
              useWidgetIdWrapper={false}
              ariaAttributes={{
                'aria-label': `show results for page ${currentPageState - 1}`
              }}
            />
          )}
          {showLeftEllipsis && (
            <ButtonLink
              onClickFunction={() => handlePaginationClick(pagesToShow[0] - 1)}
              replacementTertiaryIcon="chevron_left"
              type="plain-link"
              isButtonTag={true}
              useWidgetIdWrapper={false}
              text="..."
              ariaAttributes={{
                'aria-label': `show results for page ${pagesToShow[0] - 1}`
              }}
            />
          )}
          <ul className="non-bdo-style-list">
            {pagesToShow.map((page) => (
              <li key={page}>
                <button
                  onClick={() => handlePaginationClick(page)}
                  className={classNames('', { active: currentPageState === page })}
                >
                  {page}
                </button>
              </li>
            ))}
          </ul>
          {showRightEllipsis && (
            <ButtonLink
              onClickFunction={() => handlePaginationClick(pagesToShow[pagesToShow.length - 1] + 1)}
              replacementTertiaryIcon="chevron_left"
              type="plain-link"
              isButtonTag={true}
              useWidgetIdWrapper={false}
              text="..."
              ariaAttributes={{
                'aria-label': `show results for page ${pagesToShow[pagesToShow.length - 1] + 1}`
              }}
            />
          )}
          {currentPageState < numberOfPages && (
            <ButtonLink
              onClickFunction={() => handlePaginationClick(currentPageState + 1)}
              type="tertiary"
              isButtonTag={true}
              useWidgetIdWrapper={false}
              ariaAttributes={{
                'aria-label': `show results for page ${currentPageState + 1}`
              }}
            />
          )}
        </div>
      </PaginationStyled>
    </ServerStylesConditionalWrapper>
  );
};

export default Pagination;
